import { BackgroundImage } from 'library/ts/ui/components/background/BackgroundImage';
import { ILoadingAnimationBackgroundImage } from 'library/ts/ui/components/loading/interfaces/ILoadingAnimationBackgroundImage';
import { ILoadingAnimationBackgroundImageConfig } from 'library/ts/ui/components/loading/interfaces/ILoadingAnimationBackgroundImageConfig';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @copyright © Paramount Streaming
 * @author Daniel Silva
 * @description Loading Animation Background Image component class
 */

export class LoadingAnimationBackgroundImage implements ILoadingAnimationBackgroundImage {
	#enabled: boolean;
	#config: ILoadingAnimationBackgroundImageConfig;
	#utils: Utilities;
	#container: HTMLElement; // container for background image
	#overlayContainer: HTMLElement; // container for overlay
	#imageInstance: BackgroundImage;
	#loadingAnimationContainer: HTMLElement; // parent container where this component is appended

	/**
	 * @description Constructor
	 * @param loadingAnimationContainer {HTMLElement} parent container where this component is appended
	 * @param config {ILoadingAnimationBackgroundImageConfig} config object
	 */
	constructor( loadingAnimationContainer: HTMLElement, config: ILoadingAnimationBackgroundImageConfig ) {
		this.#loadingAnimationContainer = loadingAnimationContainer;
		this.#config = config;
		this.#utils = window.PARA.PlayerServices.Utilities;
		this.#enabled = config?.enabled;
		this.#createBackgroundImageContainer();
	}

	get name(): string {
		return 'LoadingAnimationBackgroundImage';
	}

	/**
	 * @description Enables the background image
	 */
	enable = (): void => {
		this.#enabled = true;
		this.show();
	};

	/**
	 * @description Disables and hides the background image
	 */
	disable = (): void => {
		this.#enabled = false;
		this.hide();
	};

	/**
	 * @description Shows the background image and overlay
	 */
	show = (): void => {
		if ( !this.#enabled ) {
			return;
		}
		if ( this.#utils.Type.isElement( this.#container ) ) {
			this.#container.style.display = 'block';
		}
		if ( this.#utils.Type.isElement( this.#overlayContainer ) ) {
			this.#overlayContainer.style.display = 'block';
		}
	};

	/**
	 * @description Hides the background image and overlay
	 */
	hide = (): void => {
		if ( this.#utils.Type.isElement( this.#container ) ) {
			this.#container.style.display = 'none';
		}
		if ( this.#utils.Type.isElement( this.#overlayContainer ) ) {
			this.#overlayContainer.style.display = 'none';
		}
	};

	/**
	 * @description Sets the background image using the provided URL
	 * @param imageURL {string} image URL
	 */
	setImage = ( imageURL: string ): void => {
		if ( this.#utils.URL.valid( imageURL ) ) {
			this.#config.imageURL = imageURL;

			if ( this.#utils.Type.isElement( this.#container ) ) {
				// Replace the existing background image by creating a new instance.
				this.#imageInstance = new BackgroundImage(
					this.#container,
					imageURL,
					{
						backgroundRepeat: 'no-repeat',
						backgroundSize: 'cover',
						backgroundPosition: 'center',
						opacity: 1,
						position: 'absolute',
						top: '0',
						left: '0',
						width: '100%',
						height: '100%',
						zIndex: '0'
					},
					undefined,
					undefined,
					this.#utils,
					window.SmartTag.runtimeVO
				);
			}
		}
	};

	/**
	 * @description Creates the background image container and overlay
	 */
	#createBackgroundImageContainer = (): void => {
		if ( this.#config?.enabled && this.#utils.URL.valid( this.#config?.imageURL ) ) {
			// Determine initial display style based on the enabled flag.
			const displayStyle = 'block';
			this.#container = this.#utils.DOM.stringToElement( `<div class="loading-animation-background-image" style="display: ${displayStyle};"></div>` );
			this.#loadingAnimationContainer?.appendChild( this.#container );

			const defaultOverlayOpacity: number = 0.6;
			const overlayOpacity = this.#config?.overlayOpacity ?? defaultOverlayOpacity;

			this.#overlayContainer = this.#utils.DOM.stringToElement( `
				<div style="
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background-color: rgba(0,0,0,${overlayOpacity});
					pointer-events: none;
					z-index: 1;" >
				</div>
			` );
			this.#container?.appendChild( this.#overlayContainer );

			// Create the background image instance.
			this.#imageInstance = new BackgroundImage(
				this.#container,
				this.#config?.imageURL,
				{
					backgroundRepeat: 'no-repeat',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					opacity: 1,
					position: 'absolute',
					top: '0',
					left: '0',
					width: '100%',
					height: '100%',
					zIndex: '0'
				},
				undefined,
				undefined,
				this.#utils,
				window.SmartTag.runtimeVO
			);
		}
	};
}